import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Clock,
  DollarSign,
  UserCheck,
  UserX,
  Settings,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import PensionAPIService from "@/services/pension-api";
import { formatUTCDateToBritish } from "@/lib/dateUtils";

interface Employee {
  employeeId: number;
  firstName: string;
  lastName: string;
  hasActiveEnrollment: boolean;
  eligibility: {
    isPensionEligible: boolean;
    isAutoEnrollmentRequired: boolean;
  };
}

interface PostponementStatus {
  isPostponed: boolean;
  postponementStartDate?: string;
  postponementEndDate?: string;
  catchUpRequired?: boolean;
  catchUpAmount?: number;
  isExpired?: boolean;
  lastAction?: string;
}

interface PensionStatus {
  isEnrolled: boolean;
  isOptedOut: boolean;
  isPostponed: boolean;
  postponeStartDate: string | null;
  postponeEndDate: string | null;
  autoEnrollDue: string | null;
  canOptOut: boolean;
  canOptIn: boolean;
  hasDeferredHistory: boolean;
  requiresEmployerSetup: boolean;
  lastAction: string | null;
  employerContributionPercent: number | null;
  employeeContributionPercent: number | null;
}

interface QuickActionsProps {
  employee: Employee;
  onActionComplete?: () => void;
  isEmployeeView?: boolean; // true when used by nanny/employee, false when used by employer
}

const QuickActions: React.FC<QuickActionsProps> = ({
  employee,
  onActionComplete,
  isEmployeeView = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [postponementStatus, setPostponementStatus] =
    useState<PostponementStatus | null>(null);
  const [pensionStatus, setPensionStatus] = useState<PensionStatus | null>(
    null,
  );

  // State for contribution change dialogs
  const [employerContributionPercent, setEmployerContributionPercent] =
    useState<number>(3);
  const [employeeContributionPercent, setEmployeeContributionPercent] =
    useState<number>(5);
  const [optOutReason, setOptOutReason] = useState<string>("");

  // State for pension setup form
  const [setupFormData, setSetupFormData] = useState({
    bankName: "",
    sortCode: "",
    accountNumber: "",
    hireloConsent: false,
    nestTerms: false,
    employerContributionPercent: 3,
    employeeContributionPercent: 5,
  });

  // State for pension postponement form (only consent required)
  const [postponeFormData, setPostponeFormData] = useState({
    hireloConsent: false,
    nestTerms: false,
  });

  React.useEffect(() => {
    // Fetch comprehensive pension status on component mount
    const fetchStatus = async () => {
      try {
        const [pensionStatusData, postponementStatusData] = await Promise.all([
          PensionAPIService.getPensionStatus(employee.employeeId),
          PensionAPIService.getPostponementStatus(employee.employeeId),
        ]);
        setPensionStatus(pensionStatusData);
        setPostponementStatus(postponementStatusData);
      } catch (err) {
        console.error("Error fetching pension status:", err);
      }
    };
    fetchStatus();
  }, [employee.employeeId]);

  const handleAction = async (
    actionFn: () => Promise<any>,
    successMessage: string,
  ) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await actionFn();
      setSuccess(successMessage);
      onActionComplete?.();

      // Refresh postponement status
      const status = await PensionAPIService.getPostponementStatus(
        employee.employeeId,
      );
      setPostponementStatus(status);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = () => {
    // Show auto-enrollment due date if available
    if (pensionStatus?.autoEnrollDue) {
      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          Auto-enroll due {formatUTCDateToBritish(pensionStatus.autoEnrollDue)}
        </Badge>
      );
    }

    // Show postponement status
    if (pensionStatus?.isPostponed || postponementStatus?.isPostponed) {
      if (postponementStatus?.isExpired) {
        return (
          <Badge variant="destructive" className="flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            Catch-up Required
          </Badge>
        );
      }
      const endDate =
        pensionStatus?.postponeEndDate ||
        postponementStatus?.postponementEndDate;
      return (
        <Badge variant="secondary" className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Postponed until {endDate ? formatUTCDateToBritish(endDate) : "N/A"}
        </Badge>
      );
    }

    // Show enrollment status
    if (pensionStatus?.isEnrolled || employee.hasActiveEnrollment) {
      return (
        <Badge variant="default" className="flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Active
        </Badge>
      );
    }

    if (pensionStatus?.isOptedOut) {
      return (
        <Badge variant="outline" className="flex items-center gap-1">
          <UserX className="h-3 w-3" />
          Opted Out
        </Badge>
      );
    }

    return <Badge variant="outline">Not Enrolled</Badge>;
  };

  const getAvailableActions = () => {
    const actions = [];

    // Employer-only actions (bank setup, postponement, etc.)
    if (
      !isEmployeeView &&
      !employee.hasActiveEnrollment &&
      !postponementStatus?.isPostponed
    ) {
      actions.push(
        <Dialog key="setup-pension">
          <DialogTrigger asChild>
            <Button size="sm" className="flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              Set Up Pension
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Set Up Pension</DialogTitle>
              <DialogDescription>
                Provide bank account details, consent, and contribution
                percentages to set up the pension scheme.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Bank Account Details */}
              <div className="space-y-2">
                <Label htmlFor="bank-name">Bank Name</Label>
                <Input
                  id="bank-name"
                  value={setupFormData.bankName}
                  onChange={(e) =>
                    setSetupFormData((prev) => ({
                      ...prev,
                      bankName: e.target.value,
                    }))
                  }
                  placeholder="e.g., Barclays"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="sort-code">Sort Code</Label>
                  <Input
                    id="sort-code"
                    value={setupFormData.sortCode}
                    onChange={(e) =>
                      setSetupFormData((prev) => ({
                        ...prev,
                        sortCode: e.target.value,
                      }))
                    }
                    placeholder="12-34-56"
                    maxLength={8}
                  />
                </div>
                <div>
                  <Label htmlFor="account-number">Account Number</Label>
                  <Input
                    id="account-number"
                    value={setupFormData.accountNumber}
                    onChange={(e) =>
                      setSetupFormData((prev) => ({
                        ...prev,
                        accountNumber: e.target.value,
                      }))
                    }
                    placeholder="12345678"
                    maxLength={8}
                  />
                </div>
              </div>

              {/* Contribution Percentages */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="setup-employer-percent">
                    Employer % (min 3%)
                  </Label>
                  <Input
                    id="setup-employer-percent"
                    type="number"
                    min="3"
                    max="100"
                    step="0.1"
                    value={setupFormData.employerContributionPercent}
                    onChange={(e) =>
                      setSetupFormData((prev) => ({
                        ...prev,
                        employerContributionPercent: Number(e.target.value),
                      }))
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="setup-employee-percent">
                    Employee % (min 5%)
                  </Label>
                  <Input
                    id="setup-employee-percent"
                    type="number"
                    min="5"
                    max="100"
                    step="0.1"
                    value={setupFormData.employeeContributionPercent}
                    onChange={(e) =>
                      setSetupFormData((prev) => ({
                        ...prev,
                        employeeContributionPercent: Number(e.target.value),
                      }))
                    }
                  />
                </div>
              </div>

              {/* Consent Checkboxes */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="hirelo-consent"
                    checked={setupFormData.hireloConsent}
                    onChange={(e) =>
                      setSetupFormData((prev) => ({
                        ...prev,
                        hireloConsent: e.target.checked,
                      }))
                    }
                    className="rounded"
                  />
                  <Label htmlFor="hirelo-consent" className="text-sm">
                    I allow Hirelo to create the pension scheme and contact NEST
                    on my behalf
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="nest-terms"
                    checked={setupFormData.nestTerms}
                    onChange={(e) =>
                      setSetupFormData((prev) => ({
                        ...prev,
                        nestTerms: e.target.checked,
                      }))
                    }
                    className="rounded"
                  />
                  <Label htmlFor="nest-terms" className="text-sm">
                    I agree to NEST Pensions terms & conditions
                  </Label>
                </div>
              </div>

              <Button
                onClick={() =>
                  handleAction(
                    () =>
                      PensionAPIService.setupPension(
                        employee.employeeId,
                        setupFormData,
                      ),
                    "Pension set up successfully",
                  )
                }
                disabled={
                  isLoading ||
                  !setupFormData.bankName ||
                  !setupFormData.sortCode ||
                  !setupFormData.accountNumber ||
                  !setupFormData.hireloConsent ||
                  !setupFormData.nestTerms ||
                  setupFormData.employerContributionPercent < 3 ||
                  setupFormData.employeeContributionPercent < 5
                }
                className="w-full"
              >
                Set Up Pension
              </Button>
            </div>
          </DialogContent>
        </Dialog>,
      );

      actions.push(
        <Dialog key="postpone">
          <DialogTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              <Clock className="h-4 w-4" />
              Postpone 3 Months
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                Postpone Pension for {employee.firstName}
              </DialogTitle>
              <DialogDescription>
                Postpone pension enrollment for 3 months. You'll need to provide
                consent to proceed.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="postpone-hirelo-consent"
                    checked={postponeFormData.hireloConsent}
                    onCheckedChange={(checked: boolean) =>
                      setPostponeFormData((prev) => ({
                        ...prev,
                        hireloConsent: checked === true,
                      }))
                    }
                  />
                  <div className="grid gap-1.5 leading-none">
                    <Label
                      htmlFor="postpone-hirelo-consent"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      You hereby confirm that you allow Hirelo to create your
                      pension scheme and contact Nest on your behalf
                    </Label>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="postpone-nest-terms"
                    checked={postponeFormData.nestTerms}
                    onCheckedChange={(checked: boolean) =>
                      setPostponeFormData((prev) => ({
                        ...prev,
                        nestTerms: checked === true,
                      }))
                    }
                  />
                  <div className="grid gap-1.5 leading-none">
                    <Label
                      htmlFor="postpone-nest-terms"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Please read and tick to confirm that you agree with{" "}
                      <a
                        href={PensionAPIService.getPensionTermsPdfUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Nest Pensions terms & conditions
                      </a>
                    </Label>
                  </div>
                </div>
              </div>

              <Button
                onClick={() =>
                  handleAction(
                    () =>
                      PensionAPIService.postponePension(employee.employeeId, {
                        hireloConsent: postponeFormData.hireloConsent,
                        nestTerms: postponeFormData.nestTerms,
                      }),
                    "Pension postponed for 3 months",
                  )
                }
                disabled={
                  isLoading ||
                  !postponeFormData.hireloConsent ||
                  !postponeFormData.nestTerms
                }
                className="w-full"
              >
                Postpone Pension
              </Button>
            </div>
          </DialogContent>
        </Dialog>,
      );
    }

    // Catch-up action for expired postponements (employer-only)
    if (
      !isEmployeeView &&
      postponementStatus?.isExpired &&
      postponementStatus?.catchUpRequired
    ) {
      actions.push(
        <Button
          key="catchup"
          size="sm"
          variant="default"
          onClick={() =>
            handleAction(
              () => PensionAPIService.processCatchUp(employee.employeeId),
              "Catch-up contributions processed",
            )
          }
          disabled={isLoading}
          className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700"
        >
          <DollarSign className="h-4 w-4" />
          Process Catch-up
        </Button>,
      );
    }

    // Employee actions during postponement
    if (postponementStatus?.isPostponed && !postponementStatus?.isExpired) {
      if (isEmployeeView) {
        // Simplified employee enrollment - just contribution percentage
        actions.push(
          <Dialog key="enroll-now">
            <DialogTrigger asChild>
              <Button
                size="sm"
                variant="secondary"
                className="flex items-center gap-2"
              >
                <UserCheck className="h-4 w-4" />
                Enroll Now
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Enroll in Pension Now</DialogTitle>
                <DialogDescription>
                  Choose your contribution percentage to enroll in the pension
                  scheme. Your employer will handle the setup details.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="employee-percent">
                    My Contribution % (minimum 5%)
                  </Label>
                  <Input
                    id="employee-percent"
                    type="number"
                    min="5"
                    max="100"
                    step="0.1"
                    value={employeeContributionPercent}
                    onChange={(e) =>
                      setEmployeeContributionPercent(Number(e.target.value))
                    }
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    This is the percentage of your salary that will be
                    contributed to your pension.
                  </p>
                </div>

                <Button
                  onClick={() =>
                    handleAction(
                      () =>
                        PensionAPIService.employeeEnrollNow(
                          employee.employeeId,
                          {
                            employeeContributionPercent:
                              employeeContributionPercent,
                            allow_override: true,
                          },
                        ),
                      "Enrollment request submitted",
                    )
                  }
                  disabled={isLoading || employeeContributionPercent < 5}
                  className="w-full"
                >
                  Enroll Now
                </Button>
              </div>
            </DialogContent>
          </Dialog>,
        );
      } else {
        // Full employer enrollment with bank details
        actions.push(
          <Dialog key="enroll-now">
            <DialogTrigger asChild>
              <Button
                size="sm"
                variant="secondary"
                className="flex items-center gap-2"
              >
                <UserCheck className="h-4 w-4" />
                Enroll Now
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Enroll in Pension Now</DialogTitle>
                <DialogDescription>
                  Provide bank account details, consent, and contribution
                  percentages to enroll immediately.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {/* Bank Account Details */}
                <div className="space-y-2">
                  <Label htmlFor="enroll-bank-name">Bank Name</Label>
                  <Input
                    id="enroll-bank-name"
                    value={setupFormData.bankName}
                    onChange={(e) =>
                      setSetupFormData((prev) => ({
                        ...prev,
                        bankName: e.target.value,
                      }))
                    }
                    placeholder="e.g., Barclays"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="enroll-sort-code">Sort Code</Label>
                    <Input
                      id="enroll-sort-code"
                      value={setupFormData.sortCode}
                      onChange={(e) =>
                        setSetupFormData((prev) => ({
                          ...prev,
                          sortCode: e.target.value,
                        }))
                      }
                      placeholder="12-34-56"
                      maxLength={8}
                    />
                  </div>
                  <div>
                    <Label htmlFor="enroll-account-number">
                      Account Number
                    </Label>
                    <Input
                      id="enroll-account-number"
                      value={setupFormData.accountNumber}
                      onChange={(e) =>
                        setSetupFormData((prev) => ({
                          ...prev,
                          accountNumber: e.target.value,
                        }))
                      }
                      placeholder="12345678"
                      maxLength={8}
                    />
                  </div>
                </div>

                {/* Contribution Percentages */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="enroll-employer-percent">
                      Employer % (min 3%)
                    </Label>
                    <Input
                      id="enroll-employer-percent"
                      type="number"
                      min="3"
                      max="100"
                      step="0.1"
                      value={setupFormData.employerContributionPercent}
                      onChange={(e) =>
                        setSetupFormData((prev) => ({
                          ...prev,
                          employerContributionPercent: Number(e.target.value),
                        }))
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor="enroll-employee-percent">
                      Employee % (min 5%)
                    </Label>
                    <Input
                      id="enroll-employee-percent"
                      type="number"
                      min="5"
                      max="100"
                      step="0.1"
                      value={setupFormData.employeeContributionPercent}
                      onChange={(e) =>
                        setSetupFormData((prev) => ({
                          ...prev,
                          employeeContributionPercent: Number(e.target.value),
                        }))
                      }
                    />
                  </div>
                </div>

                {/* Consent Checkboxes */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="enroll-hirelo-consent"
                      checked={setupFormData.hireloConsent}
                      onChange={(e) =>
                        setSetupFormData((prev) => ({
                          ...prev,
                          hireloConsent: e.target.checked,
                        }))
                      }
                      className="rounded"
                    />
                    <Label htmlFor="enroll-hirelo-consent" className="text-sm">
                      I allow Hirelo to create the pension scheme and contact
                      NEST on my behalf
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="enroll-nest-terms"
                      checked={setupFormData.nestTerms}
                      onChange={(e) =>
                        setSetupFormData((prev) => ({
                          ...prev,
                          nestTerms: e.target.checked,
                        }))
                      }
                      className="rounded"
                    />
                    <Label htmlFor="enroll-nest-terms" className="text-sm">
                      I agree to NEST Pensions terms & conditions
                    </Label>
                  </div>
                </div>

                <Button
                  onClick={() =>
                    handleAction(
                      () =>
                        PensionAPIService.employeeEnrollNow(
                          employee.employeeId,
                          setupFormData,
                        ),
                      "Employee enrolled immediately",
                    )
                  }
                  disabled={
                    isLoading ||
                    !setupFormData.bankName ||
                    !setupFormData.sortCode ||
                    !setupFormData.accountNumber ||
                    !setupFormData.hireloConsent ||
                    !setupFormData.nestTerms ||
                    setupFormData.employerContributionPercent < 3 ||
                    setupFormData.employeeContributionPercent < 5
                  }
                  className="w-full"
                >
                  Enroll Now
                </Button>
              </div>
            </DialogContent>
          </Dialog>,
        );
      }
    }

    // Actions for active enrollments
    if (employee.hasActiveEnrollment) {
      // Employer contribution change (employer-only)
      if (!isEmployeeView) {
        actions.push(
          <Dialog key="change-employer">
            <DialogTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="flex items-center gap-2"
              >
                <Settings className="h-4 w-4" />
                Change Employer %
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Change Employer Contribution</DialogTitle>
                <DialogDescription>
                  Set the employer contribution percentage (minimum 3%)
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="employer-percent">
                    Employer Contribution (%)
                  </Label>
                  <Input
                    id="employer-percent"
                    type="number"
                    min="3"
                    max="100"
                    step="0.1"
                    value={employerContributionPercent}
                    onChange={(e) =>
                      setEmployerContributionPercent(Number(e.target.value))
                    }
                  />
                </div>
                <Button
                  onClick={() =>
                    handleAction(
                      () =>
                        PensionAPIService.setEmployerContribution(
                          employee.employeeId,
                          employerContributionPercent,
                        ),
                      `Employer contribution changed to ${employerContributionPercent}%`,
                    )
                  }
                  disabled={isLoading || employerContributionPercent < 3}
                  className="w-full"
                >
                  Update Contribution
                </Button>
              </div>
            </DialogContent>
          </Dialog>,
        );
      }

      // Employee actions (available to both employer and employee)
      actions.push(
        <Dialog key="employee-actions">
          <DialogTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              className="flex items-center gap-2"
            >
              <UserCheck className="h-4 w-4" />
              Employee Actions
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Employee Pension Actions</DialogTitle>
              <DialogDescription>
                Actions the employee can take regarding their pension
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="employee-percent">
                  Change Employee Contribution (%)
                </Label>
                <Input
                  id="employee-percent"
                  type="number"
                  min="5"
                  max="100"
                  step="0.1"
                  value={employeeContributionPercent}
                  onChange={(e) =>
                    setEmployeeContributionPercent(Number(e.target.value))
                  }
                />
                <Button
                  onClick={() =>
                    handleAction(
                      () =>
                        PensionAPIService.setEmployeeContribution(
                          employee.employeeId,
                          employeeContributionPercent,
                        ),
                      `Employee contribution changed to ${employeeContributionPercent}%`,
                    )
                  }
                  disabled={isLoading || employeeContributionPercent < 5}
                  className="w-full"
                >
                  Update My Contribution
                </Button>
              </div>

              {/* Only show opt-out in employee view when enrolled and can opt out */}
              {isEmployeeView && pensionStatus?.canOptOut && (
                <div className="border-t pt-4 space-y-2">
                  <Label htmlFor="opt-out-reason">Opt Out Reason</Label>
                  <Select value={optOutReason} onValueChange={setOptOutReason}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select reason for opting out" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_interested">
                        Not interested in pension
                      </SelectItem>
                      <SelectItem value="other_pension">
                        Have another pension
                      </SelectItem>
                      <SelectItem value="financial_hardship">
                        Financial hardship
                      </SelectItem>
                      <SelectItem value="other">Other reason</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="destructive"
                    onClick={() =>
                      handleAction(
                        () =>
                          PensionAPIService.employeeOptOut(
                            employee.employeeId,
                            { reason: optOutReason },
                          ),
                        "Employee opted out of pension",
                      )
                    }
                    disabled={isLoading || !optOutReason}
                    className="w-full flex items-center gap-2"
                  >
                    <UserX className="h-4 w-4" />
                    Opt Out of Pension
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>,
      );
    }

    // "Opt in to pension" action - show when deferred AND previously opted-out
    // Available to both employer and employee views
    if (
      pensionStatus?.canOptIn &&
      pensionStatus?.hasDeferredHistory &&
      pensionStatus?.isOptedOut
    ) {
      actions.push(
        <Dialog key="reenroll">
          <DialogTrigger asChild>
            <Button
              size="sm"
              variant="secondary"
              className="flex items-center gap-2"
            >
              <UserCheck className="h-4 w-4" />
              Opt in to Pension
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Opt in to Pension</DialogTitle>
              <DialogDescription>
                Provide bank account details, consent, and contribution
                percentages to opt back into the pension scheme.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Bank Account Details */}
              <div className="space-y-2">
                <Label htmlFor="reenroll-bank-name">Bank Name</Label>
                <Input
                  id="reenroll-bank-name"
                  value={setupFormData.bankName}
                  onChange={(e) =>
                    setSetupFormData((prev) => ({
                      ...prev,
                      bankName: e.target.value,
                    }))
                  }
                  placeholder="e.g., Barclays"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="reenroll-sort-code">Sort Code</Label>
                  <Input
                    id="reenroll-sort-code"
                    value={setupFormData.sortCode}
                    onChange={(e) =>
                      setSetupFormData((prev) => ({
                        ...prev,
                        sortCode: e.target.value,
                      }))
                    }
                    placeholder="12-34-56"
                    maxLength={8}
                  />
                </div>
                <div>
                  <Label htmlFor="reenroll-account-number">
                    Account Number
                  </Label>
                  <Input
                    id="reenroll-account-number"
                    value={setupFormData.accountNumber}
                    onChange={(e) =>
                      setSetupFormData((prev) => ({
                        ...prev,
                        accountNumber: e.target.value,
                      }))
                    }
                    placeholder="12345678"
                    maxLength={8}
                  />
                </div>
              </div>

              {/* Contribution Percentages */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="reenroll-employer-percent">
                    Employer % (min 3%)
                  </Label>
                  <Input
                    id="reenroll-employer-percent"
                    type="number"
                    min="3"
                    max="100"
                    step="0.1"
                    value={setupFormData.employerContributionPercent}
                    onChange={(e) =>
                      setSetupFormData((prev) => ({
                        ...prev,
                        employerContributionPercent: Number(e.target.value),
                      }))
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="reenroll-employee-percent">
                    Employee % (min 5%)
                  </Label>
                  <Input
                    id="reenroll-employee-percent"
                    type="number"
                    min="5"
                    max="100"
                    step="0.1"
                    value={setupFormData.employeeContributionPercent}
                    onChange={(e) =>
                      setSetupFormData((prev) => ({
                        ...prev,
                        employeeContributionPercent: Number(e.target.value),
                      }))
                    }
                  />
                </div>
              </div>

              {/* Consent Checkboxes */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="reenroll-hirelo-consent"
                    checked={setupFormData.hireloConsent}
                    onChange={(e) =>
                      setSetupFormData((prev) => ({
                        ...prev,
                        hireloConsent: e.target.checked,
                      }))
                    }
                    className="rounded"
                  />
                  <Label htmlFor="reenroll-hirelo-consent" className="text-sm">
                    I allow Hirelo to create the pension scheme and contact NEST
                    on my behalf
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="reenroll-nest-terms"
                    checked={setupFormData.nestTerms}
                    onChange={(e) =>
                      setSetupFormData((prev) => ({
                        ...prev,
                        nestTerms: e.target.checked,
                      }))
                    }
                    className="rounded"
                  />
                  <Label htmlFor="reenroll-nest-terms" className="text-sm">
                    I agree to NEST Pensions terms & conditions
                  </Label>
                </div>
              </div>

              <Button
                onClick={() =>
                  handleAction(
                    () =>
                      PensionAPIService.employeeReenroll(
                        employee.employeeId,
                        setupFormData,
                      ),
                    "Employee re-enrolled in pension",
                  )
                }
                disabled={
                  isLoading ||
                  !setupFormData.bankName ||
                  !setupFormData.sortCode ||
                  !setupFormData.accountNumber ||
                  !setupFormData.hireloConsent ||
                  !setupFormData.nestTerms ||
                  setupFormData.employerContributionPercent < 3 ||
                  setupFormData.employeeContributionPercent < 5
                }
                className="w-full"
              >
                Re-enroll
              </Button>
            </div>
          </DialogContent>
        </Dialog>,
      );
    }

    return actions;
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">
            {employee.firstName} {employee.lastName}
          </CardTitle>
          {getStatusBadge()}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        {postponementStatus?.isPostponed &&
          postponementStatus?.catchUpAmount && (
            <Alert>
              <DollarSign className="h-4 w-4" />
              <AlertDescription>
                Catch-up amount calculated: £
                {postponementStatus.catchUpAmount.toFixed(2)}
              </AlertDescription>
            </Alert>
          )}

        <div className="flex flex-wrap gap-2">{getAvailableActions()}</div>

        {getAvailableActions().length === 0 && (
          <p className="text-sm text-muted-foreground">
            No actions available for this employee at the moment.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default QuickActions;
